import store from '../../../store'
import { PageArea, PageProps } from '../index'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ActualWorkingHoursHeader from './components/Header'
import { useActualWorkingHoursData } from './hooks/actualWorkingHoursData'
import { usePageState } from './hooks/pageState'
import { DateTerm } from '../../../utils/date'
import {
  ActualWorkingHoursRow,
  isWorkingDay,
  hasWorkingHours,
  StandardWorkingInfo,
  WorkingHoursType,
  isRequestCheckEditable,
} from './ActualWorkingHours'
import { actualWorkingHoursGridOptions } from './gridOptions'
import _ from 'lodash'
import Auth from '../../../lib/commons/auth'
import { InputError } from '../../containers/BulkSheetView/lib/validation'
import {
  MessageLevel,
  addGlobalMessage,
  addScreenMessage,
} from '../../../store/messages'
import { intl } from '../../../i18n'
import { getRowNumber } from '../../containers/BulkSheetView/lib/gridApi'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { SearchUnit } from '../../components/headers/HeaderBar/DateRangeSelector'
import { useTaskActualWorkTaskDialogState } from './hooks/taskActualWorkTaskDialogState'
import { useExcelExport } from './hooks/excelExport'
import { RouteComponentProps, withRouter } from 'react-router'
import ActualWorkingHoursView from './components/view'
import ActualWorkingHoursToolbar from './components/Toolbar'
import { ToolbarToggleValue } from '../../components/toolbars/Toolbar/ToolbarToggle'
import { Collapse } from '@mui/material'
import { useAssignedProjectsData } from './hooks/assignedProjectsData'
import { SplitButtonOptions } from '../../components/buttons/SplitButton'
import AlertDialog from '../../components/dialogs/AlertDialog'
import { useAlertDialogState } from './hooks/useAlertDialogState'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'

type PathProps = {
  code?: string
  term?: string
}

type Props = PageProps & RouteComponentProps<PathProps>

const ActualWorkingHours = (props: Props) => {
  const functionUuid = props.uuid
  const [loading, setLoading] = useState<boolean>(true)
  const [userUuid, setUserUuid] = useState<string | undefined>()
  const [userName, setUserName] = useState<string>('')
  const [openCancel, setOpenCancel] = useState<boolean>(false)
  const [toolbar, setToolbar] = useState<ToolbarToggleValue | undefined>(
    undefined
  )
  const [reloadHeader, setReloadHeader] = useState<boolean>(false)
  const [standardWorkInfo, setStandardWorkInfo] = useState<StandardWorkingInfo>(
    {
      standardWorkDays: 0,
      standardWorkHours: 0,
      totalWorkDays: 0,
      totalWorkHours: 0,
      diffDays: 0,
      diffHours: 0,
      diffDaysPercent: 0,
      diffHoursPercent: 0,
    }
  )
  const {
    data,
    summaryRow,
    ratioRow,
    refresh,
    save,
    saveRequest,
    getStandardWorkingInfo,
    updateRequestCheck,
    updateWorkingRequest,
  } = useActualWorkingHoursData()
  const pageState = usePageState({ functionUuid })
  const { taskDialogState, setTaskDialogState, openTaskActualWorkList } =
    useTaskActualWorkTaskDialogState()
  const { alertDialogState, openAlertDialog, handleSubmit, handleClose } =
    useAlertDialogState()
  const { assignedProjects } = useAssignedProjectsData({
    userUuid,
    projectUuid: undefined,
  })

  const callbackCellEdit = useCallback(() => {
    setReloadHeader(true)
  }, [])

  const gridOptions = useMemo(() => {
    return actualWorkingHoursGridOptions({
      editable: true,
      openTaskActualWorkList,
      callbackCellEdit,
    })
  }, [callbackCellEdit, openTaskActualWorkList])

  const submit = useRef<Function>()
  submit.current = save
  submit.current = saveRequest
  const reload = useRef<Function>()
  reload.current = refresh

  const initPage = useCallback(() => {
    const tenant = Auth.getCurrentTenant()
    const user = tenant!.user!
    setUserUuid(user.uuid)
    setUserName(user.name)
  }, [])

  const prevData = useRef<ActualWorkingHoursRow[]>([])
  prevData.current = data
  const hasChanged = useCallback((): boolean => {
    return (prevData.current || data).some(v => v.added || v.edited)
  }, [data])

  const refreshAll = useCallback(async () => {
    if (!gridOptions.api) return
    setLoading(true)
    try {
      const refreshRows = reload.current ?? refresh
      await refreshRows(
        userUuid,
        undefined,
        pageState.displayDateTerm.startDate,
        pageState.displayDateTerm.endDate
      )
    } finally {
      gridOptions.context = {
        ...gridOptions.context,
        errors: new InputError(),
      }
      setReloadHeader(true)
      setLoading(false)
    }
  }, [
    gridOptions,
    pageState.displayDateTerm.endDate,
    pageState.displayDateTerm.startDate,
    refresh,
    userUuid,
  ])

  const onSubmit = useCallback(async () => {
    setLoading(true)
    try {
      gridOptions.api?.stopEditing()
      if (gridOptions.context.errors.hasMessage()) {
        store.dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({ id: 'global.warning.businessError' }),
            text: gridOptions.context.errors.toMessage(id => {
              const node = gridOptions.api?.getRowNode(id)
              return !!node ? getRowNumber(node).toString() : ''
            }),
          })
        )
        return
      }
      const response = await save()
      const requestResponse = await saveRequest()

      if (
        requestResponse.json?.cancel &&
        Array.isArray(requestResponse.json.cancel) &&
        requestResponse.json.cancel.length > 0
      ) {
        const failedRequests = requestResponse.json.cancel
          .filter(cancel => cancel.failedRequestStatusUuids?.length > 0)
          .flatMap(cancel => cancel.failedRequestStatusUuids)

        if (failedRequests.length > 0) {
          const matchedData = data.filter(row =>
            row.projectRequestStatus?.some(status =>
              failedRequests.includes(status.requestUuid)
            )
          )
          const failureDetails = matchedData
            .map(row => ({
              date: row.date,
              projects:
                row.projectRequestStatus
                  ?.filter(status =>
                    failedRequests.includes(status.requestUuid!)
                  )
                  .map(
                    status =>
                      assignedProjects?.find(p => p.uuid === status.projectUuid)
                        ?.displayName
                  )
                  .filter(Boolean) || [],
            }))
            .filter(data => data.date && data.projects.length > 0)
            .map(data => {
              const [year, month, day] = data.date!.split('-').map(Number)
              return intl.formatMessage(
                {
                  id: 'actualWorkingHours.application.snackBar.message.failureDetails',
                },
                { month, day, projectDisplayName: data.projects.join(', ') }
              )
            })
            .join('\n')

          const messageText =
            intl.formatMessage({
              id: 'actualWorkingHours.application.snackBar.message',
            }) + (failureDetails ? `\n\n${failureDetails}` : '')

          store.dispatch(
            addGlobalMessage({
              type: MessageLevel.WARN,
              title: intl.formatMessage({ id: 'global.warning.businessError' }),
              text: messageText,
            })
          )
          await refreshAll()
          return
        }
      }
      if (
        !response.hasError &&
        !response.hasWarning &&
        !requestResponse.hasError &&
        !requestResponse.hasWarning
      ) {
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'registration.complete' }),
          })
        )
        await refreshAll()
      }
    } finally {
      setLoading(false)
    }
  }, [
    data,
    functionUuid,
    gridOptions.api,
    gridOptions.context.errors,
    assignedProjects,
    refreshAll,
    save,
    saveRequest,
  ])

  const onReload = useCallback(() => {
    if (hasChanged()) {
      setOpenCancel(true)
    } else {
      refreshAll()
    }
  }, [hasChanged, refreshAll])

  const callbackReload = useCallback(async () => {
    await refreshAll()
  }, [refreshAll])

  const onSearch = useCallback(
    async (unit: SearchUnit, term: DateTerm) => {
      if (unit !== pageState.searchUnit) {
        pageState.setSearchUnit(unit)
      } else if (term !== pageState.displayDateTerm) {
        pageState.setDisplayDateTerm(term)
      }
    },
    [pageState]
  )

  const onCheckRequestUntilToday = useCallback(async () => {
    const targetRows = data.filter(row => {
      return isRequestCheckEditable(row)
    })
    updateRequestCheck(targetRows, true)
  }, [data, updateRequestCheck])

  const onUncheckRequestUntilToday = useCallback(async () => {
    const targetRows = data.filter(row => {
      if (row.type !== 'WORK_HOUR') return false
      if (!isWorkingDay(row.workingHoursType as WorkingHoursType)) return false
      return row.requestCheck === true
    })
    updateRequestCheck(targetRows, false)
  }, [data, updateRequestCheck])

  const onExportExcel = useExcelExport(gridOptions, {
    dateTerm: pageState.displayDateTerm,
    userName,
    standardInfo: standardWorkInfo,
  })

  const onChangeToolbar = useCallback(
    (value: ToolbarToggleValue | undefined) => {
      setToolbar(value)
    },
    []
  )

  useKeyBind(
    [{ key: KEY_SAVE, fn: onSubmit, stopDefaultBehavior: true }],
    [data, save, saveRequest, pageState.displayDateTerm]
  )

  useEffect(() => {
    gridOptions.context = {
      ...gridOptions.context,
      errors: new InputError(),
    }
    initPage()
  }, [gridOptions, initPage])

  useEffect(() => {
    if (
      !pageState.initialized ||
      !pageState.displayDateTerm ||
      !pageState.displayDateTerm.startDate ||
      !pageState.displayDateTerm.endDate ||
      !userUuid
    ) {
      return
    }
    refreshAll()
  }, [pageState.initialized, pageState.displayDateTerm, userUuid, refreshAll])

  useEffect(() => {
    gridOptions.context = {
      ...gridOptions.context,
      userUuid,
    }
  }, [gridOptions, userUuid])

  useEffect(() => {
    if (reloadHeader) {
      const stdInfo = getStandardWorkingInfo()
      setReloadHeader(false)
      setStandardWorkInfo(stdInfo)
    }
  }, [getStandardWorkingInfo, reloadHeader])

  useEffect(() => {
    gridOptions.api?.resetRowHeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.rowHeight])

  const executeBatchRequest = useCallback(async () => {
    const targets = data.filter(
      row =>
        row.projectHours &&
        isWorkingDay(row.workingHoursType as WorkingHoursType) &&
        hasWorkingHours(row)
    )
    targets.forEach(row => {
      row.requestCheck = true
    })
    await updateWorkingRequest(targets)
    await refreshAll()
  }, [data, refreshAll, updateWorkingRequest])

  const batchMenu = useMemo(() => {
    return [
      {
        key: intl.formatMessage({
          id: 'actualWorkingHours.application.select.all',
        }),
        onClick: onCheckRequestUntilToday,
      },
      {
        key: intl.formatMessage({
          id: 'actualWorkingHours.application.deselect.all',
        }),
        onClick: onUncheckRequestUntilToday,
      },
    ] as SplitButtonOptions[]
  }, [onCheckRequestUntilToday, onUncheckRequestUntilToday])

  return (
    <>
      <AlertDialog
        isOpen={alertDialogState?.isOpen || false}
        title={alertDialogState?.title}
        message={alertDialogState?.message}
        submitHandler={handleSubmit}
        closeHandler={handleClose}
      />
      <PageArea>
        <SavePopper loading={loading} onSubmit={onSubmit} />
        <ActualWorkingHoursHeader
          onCancel={onReload}
          searchUnit={pageState.searchUnit}
          dateTerm={pageState.displayDateTerm}
          onSearch={onSearch}
          onClickExport={onExportExcel}
          toolbar={toolbar}
          onChangeToolbar={onChangeToolbar}
          rowHeight={pageState.rowHeight}
          onChangeRowHeight={(value: number) => pageState.setRowHeight(value)}
          splitButtonProps={{ options: batchMenu }}
        />
        <Collapse in={!!toolbar} timeout={100} sx={{ padding: '5px 0' }}>
          <ActualWorkingHoursToolbar
            toolbar={ToolbarToggleValue.DISPLAY}
            data={standardWorkInfo}
          />
        </Collapse>
        <ActualWorkingHoursView
          gridOptions={gridOptions}
          loading={loading}
          userUuid={userUuid}
          data={data}
          summaryRow={summaryRow}
          ratioRow={ratioRow}
          rowHeight={pageState.rowHeight}
          onCallbackReload={callbackReload}
          taskDialogState={taskDialogState}
          setTaskDialogState={setTaskDialogState}
          openTaskActualWorkList={openTaskActualWorkList}
          openCancel={openCancel}
          setOpenCancel={setOpenCancel}
        />
      </PageArea>
    </>
  )
}

export default withRouter(ActualWorkingHours)
